import {
  computeInterviewSectionOptionsForJobType
} from "./definition.js";
import {
  checkIsVoiceInterviewSectionSettings
} from "./settings.js";
const InterviewSectionStatus = {
  /** Created and waiting for the candidate to start. */
  Pending: "pending",
  /** Started by the candidate. */
  InProgress: "in_progress",
  /** Submitted by the candidate and waiting to be processed. */
  Submitted: "submitted",
  /** Processed and determined to have been completed. */
  Completed: "completed",
  /** Discarded by the candidate via a restart. Excluded from lesson status and scoring. */
  Canceled: "canceled"
};
const allInterviewSectionStatuses = [
  InterviewSectionStatus.Canceled,
  InterviewSectionStatus.Pending,
  InterviewSectionStatus.InProgress,
  InterviewSectionStatus.Submitted,
  InterviewSectionStatus.Completed
];
function compareInterviewSectionStatus(a, b) {
  return allInterviewSectionStatuses.indexOf(a) - allInterviewSectionStatuses.indexOf(b);
}
function checkAreInterviewSectionResultCallAssessmentsDue(call, sectionStatus) {
  if (!call?.completion_checked_at || !call.transcribed_at || call.is_transcript_empty) {
    return false;
  }
  return call.claimed_transcribed_at !== call.transcribed_at || sectionStatus === InterviewSectionStatus.Completed && !call.claimed_section_completed;
}
function checkIsVoiceInterviewSectionResult(result) {
  return checkIsVoiceInterviewSectionSettings(result.settings);
}
function findNextInterviewSectionOption(jobType, definitions, results) {
  const activeResults = results.filter(
    (result) => result.status !== InterviewSectionStatus.Canceled
  );
  return computeInterviewSectionOptionsForJobType(jobType, definitions).find(
    (option) => !activeResults.some(
      (result) => checkInterviewSectionResultHasSource(result, option.source)
    )
  );
}
function computeIsEveryInterviewSectionCompleted(jobType, definitions, results) {
  const isEveryExistingResultCompleted = results.every(
    (result) => result.status === InterviewSectionStatus.Completed || result.status === InterviewSectionStatus.Canceled
  );
  return isEveryExistingResultCompleted && !findNextInterviewSectionOption(jobType, definitions, results);
}
function checkInterviewSectionResultHasSource(result, source) {
  switch (source.type) {
    case "job_type":
      return result.job_type_id === source.id && // Just in case.
      result.interview_section_definition_id === null;
    case "interview_section_definition":
      return result.interview_section_definition_id === source.id;
  }
}
function serializeInterviewSectionResultForInterviewApi(result) {
  if (!result) {
    return null;
  }
  return {
    id: result.id,
    settings: result.settings,
    status: result.status
  };
}
function serializeInterviewSectionResultForAdminApi(result) {
  if (!result) {
    return null;
  }
  return {
    id: result.id,
    interview_id: result.interview_id,
    job_type_id: result.job_type_id,
    interview_section_definition_id: result.interview_section_definition_id,
    status: result.status,
    settings: result.settings
  };
}
function buildSectionResultsById(sectionResults) {
  const sectionResultsById = /* @__PURE__ */ new Map();
  for (const sectionResult of sectionResults) {
    const sectionDefinitionId = sectionResult.interview_section_definition_id;
    if (!sectionDefinitionId) {
      continue;
    }
    const existingResults = sectionResultsById.get(sectionDefinitionId) ?? [];
    sectionResultsById.set(sectionDefinitionId, [
      ...existingResults,
      sectionResult
    ]);
  }
  return sectionResultsById;
}
function findMostCompletedSectionResult(sectionResultsById, interviewSectionDefinitionId) {
  const allSectionResults = sectionResultsById.get(interviewSectionDefinitionId) ?? [];
  const mostCompletedSectionResult = allSectionResults.filter((result) => result.status !== InterviewSectionStatus.Canceled).toSorted((a, b) => compareInterviewSectionStatus(a.status, b.status)).at(-1);
  return mostCompletedSectionResult;
}
function findMostCompletedSectionResults(sectionResults) {
  const sectionResultsById = buildSectionResultsById(sectionResults);
  const mostCompletedResultIds = new Set(
    Array.from(
      sectionResultsById.keys(),
      (definitionId) => findMostCompletedSectionResult(sectionResultsById, definitionId)?.id
    )
  );
  return sectionResults.filter(
    (result) => !result.interview_section_definition_id || mostCompletedResultIds.has(result.id)
  );
}
export {
  InterviewSectionStatus,
  buildSectionResultsById,
  checkAreInterviewSectionResultCallAssessmentsDue,
  checkIsVoiceInterviewSectionResult,
  compareInterviewSectionStatus,
  computeIsEveryInterviewSectionCompleted,
  findMostCompletedSectionResult,
  findMostCompletedSectionResults,
  findNextInterviewSectionOption,
  serializeInterviewSectionResultForAdminApi,
  serializeInterviewSectionResultForInterviewApi
};
//# sourceMappingURL=result.js.map
